import { ClientProvisioner, ClientProvisionerOptions } from '@journeyapps-platform/lib-journey-core';
import * as accounts from '@journeyapps-platform/sdk-accounts-hub';
import { FlightLogClient } from '@journeyapps-platform/sdk-flight-log';
import { AccountsPortalClient } from '@journeyapps-platform/sdk-accounts';
import { AlphaClient } from '@journeyapps-platform/sdk-alpha';
import { AdminTokenProvisioner } from './AdminTokenProvisioner';
import { createWebNetworkClient } from '@journeyapps-platform/sdk-common';
import { ProviderAPIClient } from '@journeyapps-platform/sdk-source-server';
import { AzureDevopsIntrospectionClient } from '@journeyapps-platform/sdk-cerberus';

export interface AdminClientProvisionerParams extends Omit<ClientProvisionerOptions, 'token_provisioner'> {
  alpha_url: string;
  accounts_portal_url: string;
  accounts_hub_url: string;
  source_service_url: string;
  intercom_url: string;
  deploy_service_url: string;
  flightlog_url: string;
  token_provisioner: AdminTokenProvisioner;
}

export class AdminClientProvisioner extends ClientProvisioner {
  private config: AdminClientProvisionerParams;

  constructor(params: AdminClientProvisionerParams) {
    super(params);
    this.config = params;
  }

  provisionProviderClient() {
    return new ProviderAPIClient({
      endpoint: this.config.source_service_url,
      client: createWebNetworkClient({
        headers: () => {
          return this.standardHeaders();
        }
      })
    });
  }

  provisionAzureIntegrationClient(): AzureDevopsIntrospectionClient {
    return new AzureDevopsIntrospectionClient({
      endpoint: this.config.cerberus_url,
      client: createWebNetworkClient({
        headers: () => {
          return this.standardHeaders();
        }
      })
    });
  }

  async provisionSensitiveUserHubClient() {
    return new accounts.SensitiveProfileAccountsV5Client({
      endpoint: this.config.accounts_hub_url,
      client: createWebNetworkClient({
        headers: {
          Authorization: `Bearer ${await this.config.token_provisioner.sensitiveUserHubToken.getToken()}`
        }
      })
    });
  }

  async getSensitiveAccountsToken() {
    return this.config.token_provisioner.sensitiveUserHubToken.getToken();
  }

  provisionHubClientFromTrialToken(token: string) {
    return new accounts.AccountsV5Client({
      endpoint: this.config.accounts_hub_url,
      client: createWebNetworkClient({
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
    });
  }

  provisionFlightLogClient(token?: string) {
    return new FlightLogClient({
      endpoint: this.config.flightlog_url,
      client: createWebNetworkClient({
        headers: token
          ? () => {
              return {
                Authorization: `Bearer ${token}`
              };
            }
          : this.standardHeaders
      })
    });
  }

  provisionAccountsPortalClient() {
    return new AccountsPortalClient({
      endpoint: this.config.accounts_portal_url,
      client: createWebNetworkClient()
    });
  }

  provisionAlphaClient() {
    return new AlphaClient({
      endpoint: this.config.alpha_url,
      client: createWebNetworkClient({
        headers: () => {
          return this.standardHeaders();
        }
      })
    });
  }
}
